/**
 * MetricCard — Dimus Video Components
 *
 * Card de métrica estilo Dimus: CUSTO ÷ CLIENTES = PISO [TRAP]
 * Formula inline com operadores + badge colorido.
 * Compatível com o dark background do ContentOS (café, verdades, etc).
 *
 * Design ref: v9-cafe-preco-minimo.html (padrão aprovado 30/05/2026)
 * ADR: ia-skills ADR-0001
 *
 * Variações:
 * - FormulaCard: A op B = RESULT [BADGE] — cálculo/definição
 * - StatCard: valor numérico grande + label + badge de tendência
 * - ComparisonBadge: dois pills com seta entre eles
 */
import React from "react";
import { interpolate, spring, useCurrentFrame } from "remotion";

// ── Easings ──────────────────────────────────────────────────────────────────

// ── Types ─────────────────────────────────────────────────────────────────────
export interface FormulaToken {
  /** Texto do token (ex: "CUSTO", "÷", "CLIENTES", "=", "PISO") */
  text: string;
  /** Tipo visual do token */
  type: "operand" | "operator" | "result";
  /** Cor customizada. Se omitida, usa defaults por tipo */
  color?: string;
}

export interface BadgeConfig {
  text: string;
  bgColor: string;
  textColor: string;
}

/** FormulaCard: A ÷ B = RESULT [BADGE] */
export interface FormulaCardProps {
  variant: "formula";
  tokens: FormulaToken[];
  badge?: BadgeConfig;
  startFrame?: number;
  /** Estilo de fundo do card. "dark-glass" = fundo escuro semitransparente */
  cardStyle?: "dark-glass" | "transparent";
  width?: number;
}

/** StatCard: valor numérico grande + contexto */
export interface StatCardProps {
  variant: "stat";
  value: string;
  label: string;
  sublabel?: string;
  badge?: BadgeConfig;
  valueColor?: string;
  startFrame?: number;
  cardStyle?: "dark-glass" | "transparent";
  width?: number;
}

/** ComparisonBadge: A → B ou A ≠ B */
export interface ComparisonBadgeProps {
  variant: "comparison";
  left: { text: string; color: string };
  right: { text: string; color: string };
  separator: string; // ex: "→", "≠", "="
  startFrame?: number;
  cardStyle?: "dark-glass" | "transparent";
}

export type MetricCardProps = FormulaCardProps | StatCardProps | ComparisonBadgeProps;

// ── Shared card style ─────────────────────────────────────────────────────────
const cardStyles = {
  "dark-glass": {
    background: "rgba(8,10,15,.95)",
    border: "1.5px solid rgba(255,255,255,.1)",
    borderRadius: 12,
    padding: "14px 22px",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
  } as React.CSSProperties,
  transparent: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
  } as React.CSSProperties,
};

// ── Token defaults ────────────────────────────────────────────────────────────
const TOKEN_DEFAULTS = {
  operand: { color: "#666" },
  operator: { color: "#333" },
  result: { color: "#FECB18" }, // gold Dimus
};

// ── Badge component ───────────────────────────────────────────────────────────
const Badge: React.FC<{ config: BadgeConfig }> = ({ config }) => (
  <span
    style={{
      background: config.bgColor,
      color: config.textColor,
      fontWeight: 900,
      fontSize: 22,
      padding: "6px 16px",
      borderRadius: 8,
      letterSpacing: ".04em",
      fontFamily: "system-ui, -apple-system, sans-serif",
      display: "inline-block",
    }}
  >
    {config.text}
  </span>
);

// ── Entrance animation wrapper ────────────────────────────────────────────────
const WithEntrance: React.FC<{
  startFrame: number;
  children: React.ReactNode;
}> = ({ startFrame, children }) => {
  const frame = useCurrentFrame();
  const local = Math.max(0, frame - startFrame);

  const s = spring({
    frame: local,
    fps: 30,
    config: { damping: 14, stiffness: 120 },
    from: 0,
    to: 1,
  });

  const opacity = interpolate(s, [0, 0.3], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const y = interpolate(s, [0, 1], [16, 0]);

  return (
    <div
      style={{
        opacity,
        transform: `translateY(${y}px)`,
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
};

// ── FormulaCard ───────────────────────────────────────────────────────────────
const FormulaCard: React.FC<FormulaCardProps> = ({
  tokens,
  badge,
  startFrame = 0,
  cardStyle = "dark-glass",
  width,
}) => {
  return (
    <WithEntrance startFrame={startFrame}>
      <div
        style={{
          ...cardStyles[cardStyle],
          width: width ?? undefined,
        }}
      >
        {tokens.map((token, i) => {
          const defaults = TOKEN_DEFAULTS[token.type];
          return (
            <span
              key={i}
              style={{
                fontWeight: 900,
                fontSize: 26,
                color: token.color ?? defaults.color,
                fontFamily: "system-ui, -apple-system, sans-serif",
                display: "inline-block",
              }}
            >
              {token.text}
            </span>
          );
        })}

        {badge && (
          <>
            <span style={{ width: 6 }} />
            <Badge config={badge} />
          </>
        )}
      </div>
    </WithEntrance>
  );
};

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  sublabel,
  badge,
  valueColor = "#FECB18",
  startFrame = 0,
  cardStyle = "dark-glass",
  width,
}) => {
  return (
    <WithEntrance startFrame={startFrame}>
      <div
        style={{
          ...cardStyles[cardStyle],
          flexDirection: "column" as const,
          alignItems: "flex-start" as const,
          gap: 6,
          width: width ?? undefined,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontWeight: 900,
              fontSize: 52,
              color: valueColor,
              letterSpacing: "-0.04em",
              lineHeight: 1,
            }}
          >
            {value}
          </span>
          {badge && <Badge config={badge} />}
        </div>
        <span
          style={{
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: "#666",
            textTransform: "uppercase" as const,
            letterSpacing: ".06em",
          }}
        >
          {label}
        </span>
        {sublabel && (
          <span
            style={{
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontWeight: 500,
              fontSize: 16,
              color: "#444",
              letterSpacing: ".04em",
            }}
          >
            {sublabel}
          </span>
        )}
      </div>
    </WithEntrance>
  );
};

// ── ComparisonBadge ───────────────────────────────────────────────────────────
const ComparisonBadge: React.FC<ComparisonBadgeProps> = ({
  left,
  right,
  separator,
  startFrame = 0,
  cardStyle = "dark-glass",
}) => {
  const frame = useCurrentFrame();
  const local = Math.max(0, frame - startFrame);

  // Left comes from left, right from right, separator from center (opacity)
  const leftSpring = spring({ frame: local, fps: 30, config: { damping: 12, stiffness: 120 }, from: 0, to: 1 });
  const rightSpring = spring({ frame: Math.max(0, local - 8), fps: 30, config: { damping: 12, stiffness: 120 }, from: 0, to: 1 });
  const sepOp = interpolate(leftSpring, [0.5, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      {/* Left */}
      <div
        style={{
          ...cardStyles[cardStyle],
          opacity: leftSpring,
          transform: `translateX(${interpolate(leftSpring, [0, 1], [-20, 0])}px)`,
        }}
      >
        <span style={{ fontWeight: 900, fontSize: 26, color: left.color,
          fontFamily: "system-ui, -apple-system, sans-serif" }}>{left.text}</span>
      </div>

      {/* Separator */}
      <span style={{ fontWeight: 900, fontSize: 24, color: "#333",
        fontFamily: "system-ui, -apple-system, sans-serif", opacity: sepOp }}>{separator}</span>

      {/* Right */}
      <div
        style={{
          ...cardStyles[cardStyle],
          opacity: rightSpring,
          transform: `translateX(${interpolate(rightSpring, [0, 1], [20, 0])}px)`,
        }}
      >
        <span style={{ fontWeight: 900, fontSize: 26, color: right.color,
          fontFamily: "system-ui, -apple-system, sans-serif" }}>{right.text}</span>
      </div>
    </div>
  );
};

// ── Main export (dispatcher) ──────────────────────────────────────────────────
export const MetricCard: React.FC<MetricCardProps> = (props) => {
  if (props.variant === "formula") return <FormulaCard {...props} />;
  if (props.variant === "stat") return <StatCard {...props} />;
  if (props.variant === "comparison") return <ComparisonBadge {...props} />;
  return null;
};

// ── Usage Examples ────────────────────────────────────────────────────────────
/*
// FormulaCard (padrão v9-cafe):
<MetricCard
  variant="formula"
  startFrame={BEAT.dado}
  tokens={[
    { text: "CUSTO",    type: "operand" },
    { text: "÷",        type: "operator" },
    { text: "CLIENTES", type: "operand" },
    { text: "=",        type: "operator" },
    { text: "PISO",     type: "result" },
  ]}
  badge={{ text: "TRAP", bgColor: "#FECB18", textColor: "#0c0d11" }}
/>

// StatCard:
<MetricCard
  variant="stat"
  value="R$5k"
  label="PISO REAL"
  valueColor="#1FE38A"
  badge={{ text: "meta", bgColor: "#1FE38A22", textColor: "#1FE38A" }}
  startFrame={BEAT.dado + 20}
/>

// ComparisonBadge:
<MetricCard
  variant="comparison"
  left={{ text: "TRÁFEGO", color: "#FF4D4D" }}
  right={{ text: "BOCA A BOCA", color: "#00DF82" }}
  separator="→"
  startFrame={BEAT.dado}
/>
*/

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
